import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, Button, CircularProgress } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment';
import { cancelOrder, earlyEndOrder, fetchMyOrder, fetchOrder } from './api';
import DangerButton from './DangerButton';
import EarlyEndClause from './EarlyEndClause';
import { Field, TextBlock } from './OrderFields';
import { CancelOrderDialog, EarlyEndOrderDialog } from './OrderReasonDialogs';
import {
  OrderNotices,
  OrderNumberLink,
  OrderTimeline,
  OrderTripsField,
} from './OrderRecordSections';
import { ApiResult, OrderDetails, OrderSummary } from './types';
import {
  canManagerCancel,
  canManagerEndEarly,
  getAcceptedAt,
  getActionErrorDetail,
  getActionErrorMessage,
  getLifecycleStatusLabel,
  isEmploymentOrder,
} from './utils';
import * as Styles from './Orders.css';

interface OrderRowDetailsProps {
  order: OrderSummary;
  canManage: boolean;
  // My assignments: the consultant's own view of the order, without actions.
  consultantView?: boolean;
  // Called after a successful action with its success message id.
  onChanged?: (messageId: string) => void;
}

const formatDate = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY') : '—';

// Details of an order expanded below its row: everything the detail page
// shows apart from the row's own columns. In All assignments it shows the
// manager's view with End early and Cancel (change requests of 06.10 and
// 07.10); in My assignments the consultant's view (change request of 12.10).
// The detail pages stay for e-mail links.
const OrderRowDetails = ({
  order,
  canManage,
  consultantView = false,
  onChanged,
}: OrderRowDetailsProps) => {
  const intl = useIntl();
  const [details, setDetails] = useState<OrderDetails | null>(null);
  const [state, setState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [actionError, setActionError] = useState<{
    id: string;
    detail: string;
  } | null>(null);
  const reload = useCallback(() => setAttempt((value) => value + 1), []);
  // The list reloads in place after an action, so the details reload when
  // the row shows that the order changed, by this user or anyone else.
  const version = [
    order.modificationDateTime,
    order.status,
    order.successorOrderNumber,
  ].join('|');

  useEffect(() => {
    let active = true;
    setState('loading');
    const request = consultantView ? fetchMyOrder : fetchOrder;
    request(order.orderNumber).then((result) => {
      if (!active) {
        return;
      }
      if (result?.ok && result.data) {
        setDetails(result.data);
        setState('ready');
      } else {
        setState('error');
      }
    });
    return () => {
      active = false;
    };
  }, [attempt, consultantView, order.orderNumber, version]);

  const runAction = async (
    request: Promise<ApiResult<OrderDetails>>,
    messageId: string,
  ) => {
    setBusy(true);
    setActionError(null);
    const result = await request;
    setBusy(false);
    setCancelOpen(false);
    setEndOpen(false);
    if (result.ok) {
      // Show the changed order at once; the list refreshes in the background.
      if (result.data?.order) {
        setDetails(result.data);
      }
      onChanged?.(messageId);
    } else {
      setActionError({
        id: getActionErrorMessage(result.status),
        detail: getActionErrorDetail(result.status, result.message),
      });
    }
  };

  if (state === 'loading') {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress
          size={24}
          aria-label={intl.formatMessage({ id: 'ORDERS.LOADING' })}
        />
      </Box>
    );
  }

  if (state === 'error' || !details) {
    return (
      <Box py={1}>
        <Alert
          severity="error"
          action={
            <Button onClick={reload}>
              {intl.formatMessage({ id: 'ORDERS.RETRY' })}
            </Button>
          }
        >
          {intl.formatMessage({ id: 'ORDERS.DETAILS_ERROR' })}
        </Alert>
      </Box>
    );
  }

  const current = details.order;
  const events = details.events || [];
  // Employment-contract orders are not accepted by anyone.
  const acceptedAt = isEmploymentOrder(current) ? null : getAcceptedAt(events);
  const canEnd = canManage && canManagerEndEarly(current);
  const canCancel = canManage && canManagerCancel(current);
  const notifyAlso = (details.notifyAlso || []).map(({ name }) => name);

  return (
    <Box py={1}>
      {actionError && (
        <Box mb={1}>
          <Alert severity="error">
            {intl.formatMessage({ id: actionError.id })}
            {actionError.detail && <div>{actionError.detail}</div>}
          </Alert>
        </Box>
      )}
      <OrderNotices
        details={details}
        consultantView={consultantView}
        spacing={1}
      />
      <Styles.DetailGrid>
        <Field
          label={intl.formatMessage({ id: 'ORDERS.OWNER' })}
          value={current.ownerName}
        />
        <Field
          label={intl.formatMessage({ id: 'ORDERS.CREATED_BY' })}
          value={current.createdByName}
        />
        <Field
          label={intl.formatMessage({ id: 'ORDERS.ISSUED_AT' })}
          value={moment(current.creationDateTime).format('DD/MM/YYYY HH:mm')}
        />
        {acceptedAt && (
          <Field
            label={intl.formatMessage({ id: 'ORDERS.ACCEPTED_AT' })}
            value={moment(acceptedAt).format('DD/MM/YYYY HH:mm')}
          />
        )}
        {current.activationDate && (
          <Field
            label={intl.formatMessage({ id: 'ORDERS.ACTIVATION_DATE' })}
            value={formatDate(current.activationDate)}
          />
        )}
        {current.effectiveEndDate && (
          <Field
            label={intl.formatMessage({ id: 'ORDERS.EFFECTIVE_END_DATE' })}
            value={formatDate(current.effectiveEndDate)}
          />
        )}
        {consultantView && (
          <Field
            label={intl.formatMessage({ id: 'ORDERS.CONTRACT_TYPE' })}
            value={intl.formatMessage({
              id: `ORDERS.CONTRACT.${current.contractType}`,
            })}
          />
        )}
        <Field
          label={intl.formatMessage({ id: 'ORDERS.LIFECYCLE_STATUS' })}
          value={intl.formatMessage({
            id: getLifecycleStatusLabel(
              current.lifecycleStatus,
              current.contractType,
            ),
          })}
        />
        {current.predecessorOrderNumber && (
          <Field
            label={intl.formatMessage({ id: 'ORDERS.PREDECESSOR' })}
            value={
              <OrderNumberLink
                number={current.predecessorOrderNumber}
                consultantView={consultantView}
              />
            }
          />
        )}
        {current.successorOrderNumber && (
          <Field
            label={intl.formatMessage({ id: 'ORDERS.SUCCESSOR' })}
            value={
              <OrderNumberLink
                number={current.successorOrderNumber}
                consultantView={consultantView}
              />
            }
          />
        )}
        {!consultantView && (
          <Field
            label={intl.formatMessage({ id: 'ORDERS.NOTIFY_ALSO' })}
            value={notifyAlso.join(', ')}
          />
        )}
        <OrderTripsField order={current} consultantView={consultantView} />
      </Styles.DetailGrid>
      {details.subject?.trim() && (
        <TextBlock
          label={intl.formatMessage({ id: 'ORDERS.SUBJECT' })}
          value={details.subject.trim()}
        />
      )}
      <TextBlock
        label={intl.formatMessage({ id: 'ORDERS.CONDITIONS' })}
        value={details.conditions?.trim() || '—'}
      />
      {consultantView && <EarlyEndClause />}
      {(canEnd || canCancel) && (
        <Box
          mt={2}
          display="flex"
          justifyContent="flex-end"
          flexWrap="wrap"
          gridGap={8}
        >
          {canEnd && (
            <Button
              color="secondary"
              variant="outlined"
              disabled={busy}
              onClick={() => setEndOpen(true)}
            >
              {intl.formatMessage({ id: 'ORDERS.END_EARLY' })}
            </Button>
          )}
          {canCancel && (
            <DangerButton disabled={busy} onClick={() => setCancelOpen(true)}>
              {intl.formatMessage({ id: 'ORDERS.CANCEL_ORDER' })}
            </DangerButton>
          )}
        </Box>
      )}
      <OrderTimeline details={details} consultantView={consultantView} />
      {canCancel && (
        <CancelOrderDialog
          open={cancelOpen}
          busy={busy}
          onClose={() => setCancelOpen(false)}
          onSubmit={(reason) =>
            runAction(
              cancelOrder(current.orderNumber, { reason }),
              'ORDERS.SUCCESS.CANCEL',
            )
          }
        />
      )}
      {canEnd && (
        <EarlyEndOrderDialog
          open={endOpen}
          busy={busy}
          endDate={current.endDate}
          onClose={() => setEndOpen(false)}
          onSubmit={(request) =>
            runAction(
              earlyEndOrder(current.orderNumber, request),
              'ORDERS.SUCCESS.EARLY_END',
            )
          }
        />
      )}
    </Box>
  );
};

export default OrderRowDetails;
